import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import MainLayout from "@/components/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { UserPlus, Trash2, Edit2, Shield, Eye, Pencil, KeyRound } from "lucide-react";

export default function Team() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [newMember, setNewMember] = useState({
    email: "",
    name: "",
    password: "",
    role: "viewer"
  });
  const [editForm, setEditForm] = useState({
    name: "",
    role: "",
    password: ""
  });

  const fetchMembers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setMembers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You don't have permission to manage team members");
      } else {
        toast.error("Failed to load team members");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!newMember.email || !newMember.password) {
      toast.error("Email and password are required");
      return;
    }
    try {
      await axios.post(`${API}/team`, newMember);
      toast.success("Team member added successfully");
      setShowAddDialog(false);
      setNewMember({ email: "", name: "", password: "", role: "viewer" });
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add team member");
    }
  };

  const handleEditMember = async (e) => {
    e.preventDefault();
    if (!editingMember) return;
    
    const updates = {};
    if (editForm.name !== editingMember.name) updates.name = editForm.name;
    if (editForm.role !== editingMember.role) updates.role = editForm.role;
    if (editForm.password) updates.password = editForm.password;
    
    if (Object.keys(updates).length === 0) {
      toast.info("No changes to save");
      return;
    }
    
    try {
      await axios.put(`${API}/team/${editingMember.id}`, updates);
      toast.success("Team member updated successfully");
      setShowEditDialog(false);
      setEditingMember(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update team member");
    }
  };

  const handleDeleteMember = async (memberId, memberName) => {
    if (!confirm(`Are you sure you want to remove ${memberName} from your team?`)) return;
    
    try {
      await axios.delete(`${API}/team/${memberId}`);
      toast.success("Team member removed successfully");
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove team member");
    }
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    setEditForm({
      name: member.name,
      role: member.role,
      password: ""
    });
    setShowEditDialog(true);
  };

  const getRoleBadge = (role) => {
    const badges = {
      owner: { icon: Shield, color: "bg-purple-500/20 text-purple-400", label: "Owner" },
      editor: { icon: Pencil, color: "bg-blue-500/20 text-blue-400", label: "Editor" },
      viewer: { icon: Eye, color: "bg-gray-500/20 text-gray-400", label: "Viewer" }
    };
    const badge = badges[role] || badges.viewer;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  return (
    <MainLayout title="Team Management" subtitle="Manage your team members and their permissions">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="bg-white/5 border border-white/10 rounded-lg p-4 max-w-lg">
            <h3 className="text-sm font-medium text-white mb-2">Role Permissions</h3>
            <div className="space-y-1.5 text-xs text-gray-400">
              <div className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-purple-400" />
                <span><strong className="text-white">Owner:</strong> Full control, manage team</span>
              </div>
              <div className="flex items-center gap-2">
                <Pencil className="w-3.5 h-3.5 text-blue-400" />
                <span><strong className="text-white">Editor:</strong> Edit banners (no destination URL or snippet)</span>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="w-3.5 h-3.5 text-gray-400" />
                <span><strong className="text-white">Viewer:</strong> View-only access</span>
              </div>
            </div>
          </div>
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-[#10B981] hover:bg-[#059669] text-white"
            data-testid="add-team-member-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Team Member
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#10B981]"></div>
          </div>
        ) : members.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
            <UserPlus className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No team members yet</h3>
            <p className="text-gray-400 mb-4">Add team members to collaborate on your banners</p>
            <Button
              onClick={() => setShowAddDialog(true)}
              className="bg-[#10B981] hover:bg-[#059669] text-white"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Your First Team Member
            </Button>
          </div>
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-gray-400">Name</TableHead>
                  <TableHead className="text-gray-400">Email</TableHead>
                  <TableHead className="text-gray-400">Role</TableHead>
                  <TableHead className="text-gray-400">Created</TableHead>
                  <TableHead className="text-gray-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id} className="border-white/10 hover:bg-white/5" data-testid={`team-member-${member.id}`}>
                    <TableCell className="text-white font-medium">{member.name || "-"}</TableCell>
                    <TableCell className="text-gray-300">{member.email}</TableCell>
                    <TableCell>{getRoleBadge(member.role)}</TableCell>
                    <TableCell className="text-gray-400">
                      {new Date(member.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(member)}
                          className="text-gray-400 hover:text-white"
                          data-testid={`edit-member-${member.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteMember(member.id, member.name || member.email)}
                          className="text-gray-400 hover:text-red-400"
                          data-testid={`delete-member-${member.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Add Member Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="bg-[#1a1a24] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Add Team Member</DialogTitle>
              <DialogDescription className="text-gray-400">
                Create a new team member with a temporary password
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddMember} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                  placeholder="member@example.com"
                  className="bg-white/5 border-white/10 text-white"
                  required
                  data-testid="new-member-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={newMember.name}
                  onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  placeholder="John Doe"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="new-member-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Temporary Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={newMember.password}
                  onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                  placeholder="Create a password"
                  className="bg-white/5 border-white/10 text-white"
                  required
                  data-testid="new-member-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  value={newMember.role}
                  onValueChange={(value) => setNewMember({ ...newMember, role: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="new-member-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a24] border-white/10">
                    <SelectItem value="editor" className="text-white hover:bg-white/10">
                      <div className="flex items-center gap-2">
                        <Pencil className="w-3.5 h-3.5 text-blue-400" />
                        Editor - Can edit and delete banners
                      </div>
                    </SelectItem>
                    <SelectItem value="viewer" className="text-white hover:bg-white/10">
                      <div className="flex items-center gap-2">
                        <Eye className="w-3.5 h-3.5 text-gray-400" />
                        Viewer - View-only access
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} className="border-white/10 text-white hover:bg-white/10">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="submit-new-member">
                  Add Member
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Member Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-[#1a1a24] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Edit Team Member</DialogTitle>
              <DialogDescription className="text-gray-400">
                Update {editingMember?.email}'s details
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditMember} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="John Doe"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="edit-member-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-role">Role</Label>
                <Select
                  value={editForm.role}
                  onValueChange={(value) => setEditForm({ ...editForm, role: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="edit-member-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a24] border-white/10">
                    <SelectItem value="editor" className="text-white hover:bg-white/10">
                      <div className="flex items-center gap-2">
                        <Pencil className="w-3.5 h-3.5 text-blue-400" />
                        Editor
                      </div>
                    </SelectItem>
                    <SelectItem value="viewer" className="text-white hover:bg-white/10">
                      <div className="flex items-center gap-2">
                        <Eye className="w-3.5 h-3.5 text-gray-400" />
                        Viewer
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-password" className="flex items-center gap-2">
                  <KeyRound className="w-3.5 h-3.5" />
                  New Password (optional)
                </Label>
                <Input
                  id="edit-password"
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="Leave blank to keep current password"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="edit-member-password"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)} className="border-white/10 text-white hover:bg-white/10">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="submit-edit-member">
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
